import { useEffect, useRef } from "react";
import { Stack } from "@mui/material";
import { DashboardFilterInstance, FilterDefinition } from "../../../../../context/FiltersProvider/types";
import { DashboardFilterChip } from "./DashboardFilterChip";
import { DashboardFilterDropdown } from "./DashboardFilterDropdown";

type Props = {
	filter: FilterDefinition;
	instance: DashboardFilterInstance;
	disabled?: boolean;

	onUpdate: (instanceId: string, patch: Partial<DashboardFilterInstance>) => void;
	onRemove: (instanceId: string) => void;
};

export const DashboardFilterItem = ({ filter, instance, disabled, onUpdate, onRemove }: Props) => {
	const ref = useRef<HTMLDivElement>(null);

	// toggle open only chip
	const handleToggle = () => {
		onUpdate(instance.id, {
			open: !instance.open,
		});
	};

	// Close on outside click
	// - safe per MUI portal
	// - avoid to close dropdown on select
	useEffect(() => {
		if (!instance.open) return;

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;

			if (!ref.current) return;

			const clickedInside = ref.current.contains(target);

			// MUI renders menus in portals
			const clickedInMuiLayer =
				!!target.closest(".MuiPopover-root") ||
				!!target.closest(".MuiMenu-root") ||
				!!target.closest(".MuiModal-root");

			if (!clickedInside && !clickedInMuiLayer) {
				onUpdate(instance.id, {
					open: false,
				});
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [instance.open, instance.id, onUpdate]);

	return (
		<Stack ref={ref} position="relative" direction="row" alignItems="center">
			<DashboardFilterChip
				instance={instance}
				filter={filter}
				onToggle={handleToggle}
				disabled={disabled}
				onUpdate={onUpdate}
				onRemove={onRemove}
			/>

			{instance.open && <DashboardFilterDropdown instance={instance} filter={filter} onUpdate={onUpdate} />}
		</Stack>
	);
};
